import type { FC, KeyboardEvent, PointerEvent, ReactNode } from 'react'
import type { DashboardRange, LoadPoint } from '@/constants/dashboard'

import { useId, useState } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'

import {
    areaToBaselinePath,
    bandPath,
    linePath,
    niceTicks
} from '@/lib/chart'

interface ForecastChartProps {
    range: DashboardRange
}

const WIDTH = 1000
const HEIGHT = 300

const formatter = new Intl.NumberFormat('en-US')

const mw = (value: number): string => `${formatter.format(value)} MW`

const describePoint = (point: LoadPoint): string => {
    const actual = point.actual === null ? 'no actual reading yet' : `actual ${mw(point.actual)}`

    return `${point.label}: forecast ${mw(point.forecast)}, ${actual}, expected range ${mw(point.low)} to ${mw(point.high)}.`
}

const ForecastChart: FC<ForecastChartProps> = ({ range }): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const instructionsId = useId()

    const { points, nowIndex, tickEvery } = range
    const count = points.length

    const values = points.flatMap((point) => [point.low, point.high, point.actual ?? point.forecast])
    const ticks = niceTicks(Math.min(...values), Math.max(...values), 4)
    const domainMin = ticks[0]
    const domainMax = ticks[ticks.length - 1]

    const xAt = (index: number): number => (count < 2 ? WIDTH / 2 : (index / (count - 1)) * WIDTH)
    const yAt = (value: number): number => HEIGHT - ((value - domainMin) / (domainMax - domainMin)) * HEIGHT
    const xPercent = (index: number): number => (xAt(index) / WIDTH) * 100
    const yPercent = (value: number): number => (yAt(value) / HEIGHT) * 100

    const forecastLine = points.map((point, index) => ({ x: xAt(index), y: yAt(point.forecast) }))
    const upper = points.map((point, index) => ({ x: xAt(index), y: yAt(point.high) }))
    const lower = points.map((point, index) => ({ x: xAt(index), y: yAt(point.low) }))

    const actualLine = points.flatMap((point, index) => (
        point.actual === null ? [] : [{ x: xAt(index), y: yAt(point.actual) }]
    ))

    const hasActual = actualLine.length > 1

    const peakIndex = points.reduce((best, point, index) => (point.forecast > points[best].forecast ? index : best), 0)
    const peak = points[peakIndex]
    const peakX = xPercent(peakIndex)

    const activePoint = active === null ? null : points[active]
    const activeX = active === null ? 0 : xPercent(active)

    const indexFromPointer = (event: PointerEvent<HTMLDivElement>): number => {
        const rect = event.currentTarget.getBoundingClientRect()
        const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))

        return Math.round(fraction * (count - 1))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const current = active ?? nowIndex ?? peakIndex

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            setActive(Math.min(count - 1, current + (active === null ? 0 : 1)))
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            setActive(Math.max(0, current - (active === null ? 0 : 1)))
        }
        else if (event.key === 'Home') {
            event.preventDefault()
            setActive(0)
        }
        else if (event.key === 'End') {
            event.preventDefault()
            setActive(count - 1)
        }
        else if (event.key === 'Escape') {
            setActive(null)
        }
    }

    return (
        <div>
            <ul className='flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground'>
                {hasActual && (
                    <li className='flex items-center gap-2'>
                        <span aria-hidden='true' className='h-0.5 w-4 rounded-full bg-primary' />
                        Actual
                    </li>
                )}

                <li className='flex items-center gap-2'>
                    <svg aria-hidden='true' viewBox='0 0 16 2' className='h-0.5 w-4'>
                        <line x1='0' y1='1' x2='16' y2='1' strokeWidth='2' strokeDasharray='4 3' className='stroke-chart-2' />
                    </svg>
                    Forecast
                </li>

                <li className='flex items-center gap-2'>
                    <span aria-hidden='true' className='h-2.5 w-4 rounded-[3px] bg-chart-2/10' />
                    Expected range
                </li>

                <li className='flex items-center gap-2'>
                    <span aria-hidden='true' className='size-2.5 rounded-full bg-sun ring-2 ring-white' />
                    Forecast peak
                </li>
            </ul>

            <div className='relative mt-3 h-60 pb-7 pl-12 pt-9 sm:h-72'>
                <div aria-hidden='true' className='absolute bottom-7 left-0 top-9 w-10'>
                    {ticks.map((tick) => (
                        <span
                            key={tick}
                            className='absolute right-0 -translate-y-1/2 text-[11px] tabular-nums text-muted-foreground'
                            style={{ top: `${yPercent(tick)}%` }}
                        >
                            {formatter.format(tick)}
                        </span>
                    ))}
                </div>

                <div className='relative h-full'>
                    <motion.svg
                        aria-hidden='true'
                        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                        preserveAspectRatio='none'
                        className='absolute inset-0 size-full'
                        initial={{ clipPath: 'inset(-10% 100% -10% 0%)' }}
                        whileInView={{ clipPath: 'inset(-10% 0% -10% 0%)' }}
                        viewport={{ once: true, margin: '0px 0px -15% 0px' }}
                        transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
                    >
                        {ticks.map((tick) => (
                            <line
                                key={tick}
                                x1={0}
                                x2={WIDTH}
                                y1={yAt(tick)}
                                y2={yAt(tick)}
                                strokeWidth={1}
                                vectorEffect='non-scaling-stroke'
                                className='stroke-border'
                            />
                        ))}

                        <path d={bandPath(upper, lower)} className='fill-chart-2/[0.07]' />

                        {hasActual && (
                            <path d={areaToBaselinePath(actualLine, HEIGHT)} className='fill-primary/10' />
                        )}

                        <path
                            d={linePath(forecastLine)}
                            fill='none'
                            strokeWidth={2}
                            strokeDasharray='6 6'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            vectorEffect='non-scaling-stroke'
                            className='stroke-chart-2'
                        />

                        {hasActual && (
                            <path
                                d={linePath(actualLine)}
                                fill='none'
                                strokeWidth={2}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                vectorEffect='non-scaling-stroke'
                                className='stroke-primary'
                            />
                        )}
                    </motion.svg>

                    {nowIndex !== null && (
                        <div
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-y-0 w-px bg-foreground/20'
                            style={{ left: `${xPercent(nowIndex)}%` }}
                        >
                            <span className='absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium text-white'>
                                Now
                            </span>
                        </div>
                    )}

                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute'
                        style={{ left: `${peakX}%`, top: `${yPercent(peak.forecast)}%` }}
                    >
                        <span className='absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sun ring-2 ring-white' />

                        <span
                            className={cn(
                                'absolute bottom-3 whitespace-nowrap rounded-lg bg-white px-2 py-1 text-[11px] font-medium text-foreground shadow-sm ring-1 ring-black/5 transition-opacity',
                                peakX > 80 ? '-translate-x-full' : peakX < 20 ? 'translate-x-0' : '-translate-x-1/2',
                                active !== null && 'opacity-0'
                            )}
                        >
                            Peak {peak.label}, {mw(peak.forecast)}
                        </span>
                    </div>

                    {activePoint && (
                        <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                            <div
                                className='absolute inset-y-0 w-px bg-foreground/30'
                                style={{ left: `${activeX}%` }}
                            />

                            <span
                                className='absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-chart-2 ring-2 ring-white'
                                style={{ left: `${activeX}%`, top: `${yPercent(activePoint.forecast)}%` }}
                            />

                            {activePoint.actual !== null && (
                                <span
                                    className='absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-2 ring-white'
                                    style={{ left: `${activeX}%`, top: `${yPercent(activePoint.actual)}%` }}
                                />
                            )}

                            <div
                                className={cn(
                                    'absolute top-1 w-44 rounded-xl bg-white p-3 text-xs shadow-[0_16px_32px_-16px_rgba(14,26,18,0.45)] ring-1 ring-black/5',
                                    activeX > 55 ? '-translate-x-[calc(100%+12px)]' : 'translate-x-3'
                                )}
                                style={{ left: `${activeX}%` }}
                            >
                                <p className='font-medium text-muted-foreground'>
                                    {activePoint.label}
                                </p>

                                <div className='mt-2 space-y-1.5'>
                                    {activePoint.actual !== null && (
                                        <div className='flex items-center gap-2'>
                                            <span className='h-0.5 w-3 shrink-0 rounded-full bg-primary' />
                                            <span className='font-semibold text-foreground'>{mw(activePoint.actual)}</span>
                                            <span className='text-muted-foreground'>Actual</span>
                                        </div>
                                    )}

                                    <div className='flex items-center gap-2'>
                                        <span className='h-0.5 w-3 shrink-0 rounded-full bg-chart-2' />
                                        <span className='font-semibold text-foreground'>{mw(activePoint.forecast)}</span>
                                        <span className='text-muted-foreground'>Forecast</span>
                                    </div>

                                    <div className='flex items-center gap-2'>
                                        <span className='h-2 w-3 shrink-0 rounded-[2px] bg-chart-2/15' />
                                        <span className='font-medium text-foreground'>
                                            {formatter.format(activePoint.low)} to {formatter.format(activePoint.high)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div
                        role='group'
                        tabIndex={0}
                        aria-roledescription='chart'
                        aria-label={`Load forecast versus actual, ${range.chartSubtitle.toLowerCase()}`}
                        aria-describedby={instructionsId}
                        onPointerMove={(event) => setActive(indexFromPointer(event))}
                        onPointerDown={(event) => setActive(indexFromPointer(event))}
                        onPointerLeave={() => setActive(null)}
                        onKeyDown={onKeyDown}
                        onBlur={() => setActive(null)}
                        className='absolute inset-0 cursor-crosshair touch-pan-y rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4'
                    />

                    <p id={instructionsId} className='sr-only'>
                        Use the left and right arrow keys to move through the chart.
                    </p>

                    <p className='sr-only' aria-live='polite'>
                        {activePoint ? describePoint(activePoint) : ''}
                    </p>
                </div>

                <div aria-hidden='true' className='absolute bottom-0 left-12 right-0 h-5'>
                    {points.map((point, index) => {
                        if (index % tickEvery !== 0) return null

                        const left = xPercent(index)

                        return (
                            <span
                                key={point.label}
                                className={cn(
                                    'absolute top-0 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground',
                                    left === 0 ? 'translate-x-0' : left === 100 ? '-translate-x-full' : '-translate-x-1/2'
                                )}
                                style={{ left: `${left}%` }}
                            >
                                {point.label}
                            </span>
                        )
                    })}
                </div>
            </div>

            <table className='sr-only'>
                <caption>
                    Load forecast versus actual, {range.chartSubtitle.toLowerCase()}
                </caption>

                <thead>
                    <tr>
                        <th scope='col'>Time</th>
                        <th scope='col'>Forecast (MW)</th>
                        <th scope='col'>Actual (MW)</th>
                        <th scope='col'>Expected low (MW)</th>
                        <th scope='col'>Expected high (MW)</th>
                    </tr>
                </thead>

                <tbody>
                    {points.map((point) => (
                        <tr key={point.label}>
                            <th scope='row'>{point.label}</th>
                            <td>{formatter.format(point.forecast)}</td>
                            <td>{point.actual === null ? 'Not yet available' : formatter.format(point.actual)}</td>
                            <td>{formatter.format(point.low)}</td>
                            <td>{formatter.format(point.high)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default ForecastChart
